import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Event, ICustomerForm, TimeSlot } from "../../core/types/types";
import BusinessCustomerRoute from "../BusinessCustomerRoute/BusinessCustomerRoute";
import AddressAutocomplete from "../AddressAutocomplete/AddressAutocomplete";
import flavourConfig from "../../environment/flavourConfig";
import { useTimeSlots } from "../../hooks/useTimeSlots";
import CircleSpinner from "../CircleSpinner/CircleSpinner";
import { formatDate, formatTimeRange } from "../../utils/dateTimeUtils";

const CustomerForm: React.FC<ICustomerForm> = ({
  eventList,
  customer,
  updateCustomer,
  isResidentialCustomer,
  updateIsResidentialCustomer,
  isValidAddress,
  updateIsValidAddress,
  updateHasCompletedCustomerPage,
  hasTickedPrivacyPolicy,
  updateHasTickedPrivacyPolicy,
  updateFormId,
  updateIsSubmitButtonDisabled,
}: ICustomerForm) => {
  const history = useHistory();
  const formId = "details-signup";

  // Set 'Next' button to use this form id
  useEffect(() => {
    updateFormId(formId);
  }, [updateFormId]);

  // Form error messages
  const [residentialCustomerError, updateResidentialCustomerError] = useState<
    string | null
  >(null);
  const [addressErrorMessage, updateAddressErrorMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    const isDisabled: boolean =
      isResidentialCustomer !== true ||
      residentialCustomerError !== null ||
      addressErrorMessage !== null ||
      !eventList.length;
    updateIsSubmitButtonDisabled(isDisabled);
  }, [
    isResidentialCustomer,
    residentialCustomerError,
    addressErrorMessage,
    eventList,
    updateIsSubmitButtonDisabled,
  ]);

  var {
    timeSlots,
    isLoading: timeSlotsLoading,
    error: timeSlotsError,
  } = useTimeSlots(customer.Event);

  // Main submit method
  const handleSubmit = async () => {
    updateHasCompletedCustomerPage(true);
    history.push("/waste-form");
  };

  // Displays the event list within a <select> tag
  const renderEventList = (event: Event, index: number) => {
    let eventThresholdError = "";
    let isDisabled = false;

    if (event.percentageFull >= 100) {
      eventThresholdError = "(Event is at capacity. Bookings are now closed.)";
      isDisabled = true;
    } else if (event.percentageFull >= 80) {
      eventThresholdError = "(Event is nearly full.)";
    }

    return (
      <option
        value={event.eventID}
        key={index}
        disabled={isDisabled}
        className={`${isDisabled && "text-red-500"}`}
      >
        {`${event.eventName}, ${formatTimeRange(
          event.startDateTime,
          event.endDateTime,
        )} on ${formatDate(event.endDateTime)} ${eventThresholdError}`}
      </option>
    );
  };

  const renderTimeSlotList = (timeSlot: TimeSlot, index: number) => {
    const isAvailable = timeSlot.isAvailable;
    const label = `${formatTimeRange(timeSlot.startTime, timeSlot.endTime)}${
      isAvailable ? "" : "  - ( Booking Full )"
    }`;
    return (
      <option
        value={timeSlot.timeSlotID}
        key={index}
        disabled={!isAvailable}
        className={isAvailable ? "" : "text-red-500"}
      >
        {label}
      </option>
    );
  };

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        if (isResidentialCustomer === null) {
          updateResidentialCustomerError(
            "You must select one of the above options.",
          );
          updateResidentialCustomerError(
            "You must select one of the above options.",
          );
          return;
        }
        handleSubmit();
      }}
    >
      <div className="mb-4 xl:mb-6 w-full flex flex-col items-start justify-center">
        <h1 className="body-heading-font">Enter Your Details</h1>
        <p className="body-subheading-font mt-2">
          Please fill in the form below with your details. You can click on the
          information icon in the top right corner if you need help.
        </p>
        <p className="pb-1.5 xl:pb-2 form-subheading-font mt-10 xl:mt-12">
          Select a Customer Type<span className="text-red-600">*</span>
        </p>
        <div className="flex w-full justify-center">
          <div className="bg-transparent border-2 border-gray-200 rounded flex items-center">
            <button
              type="button"
              onClick={() => {
                updateIsResidentialCustomer(true);
                updateResidentialCustomerError(null);
              }}
              className={`border-r border-gray-200 text-center px-4 xl:px-10 py-3 lg:py-5 flex justify-center items-center cursor-pointer focus:bg-${
                flavourConfig.inputColorLight
              } hover:bg-${flavourConfig.inputColorLightest}
                        ${
                          isResidentialCustomer !== null &&
                          isResidentialCustomer &&
                          `bg-${flavourConfig.inputColorLight}`
                        }`}
            >
              <p className="body-input-font">I'm a Residential Customer</p>
            </button>
            <button
              type="button"
              onClick={() => {
                updateIsResidentialCustomer(false);
                updateResidentialCustomerError(null);
              }}
              className={`text-center px-4 xl:px-10 py-3 lg:py-5 flex justify-center items-center cursor-pointer focus:bg-${
                flavourConfig.inputColorLight
              } hover:bg-${flavourConfig.inputColorLightest}
                        ${
                          isResidentialCustomer !== null &&
                          !isResidentialCustomer &&
                          `bg-${flavourConfig.inputColorLight}`
                        }`}
            >
              <p className="body-input-font">I'm a Business Customer</p>
            </button>
          </div>
        </div>
        {residentialCustomerError && (
          <p className="body-error p-4 text-center w-full">
            {residentialCustomerError}
          </p>
        )}
      </div>
      {isResidentialCustomer === false ? (
        <BusinessCustomerRoute />
      ) : (
        <div className="w-full flex flex-col items-end">
          <div className="w-full md:flex flex-wrap justify-between">
            <div className="w-full flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="event"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Select an Event<span className="text-red-600">*</span>
              </label>
              <div className="border border-gray-300 shadow-sm rounded flex relative w-full">
                <select
                  name="event"
                  required
                  id="event"
                  value={customer.Event === 0 ? "" : customer.Event}
                  className={`body-input-font background-white-ie-fix appearance-none z-10 py-3 pl-3 pr-12 overflow-ellipsis whitespace-nowrap overflow-x-hidden w-full border border-transparent focus:outline-none focus:border-${flavourConfig.inputColor} rounded`}
                  onChange={(e) =>
                    updateCustomer({
                      ...customer,
                      Event: parseInt(e.target.value),
                    })
                  }
                >
                  <option disabled value=""></option>
                  {eventList.map(renderEventList)}
                </select>
                <div
                  className="px-4 flex items-center border-l border-gray-300 flex-col justify-center text-gray-500
                        absolute right-0 bottom-0 top-0 mx-auto z-20 pointer-events-none "
                >
                  <svg
                    tabIndex={-1}
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-chevron-up"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" />
                    <polyline points="6 15 12 9 18 15" />
                  </svg>
                  <svg
                    tabIndex={-1}
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-chevron-down"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" />
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>
            {timeSlotsLoading ? (
              <div className="w-full h-14 flex items-center">
                <CircleSpinner />
              </div>
            ) : timeSlotsError ? (
              <p className="mt-3 body-error">{timeSlotsError}</p>
            ) : (
              <div className="w-full flex flex-col mb-4 xl:mb-6">
                <label
                  htmlFor="event"
                  className="pb-1.5 xl:pb-2 form-subheading-font"
                >
                  Select a Time Slot
                  <span className="text-red-600">*</span>
                </label>
                <div className="border border-gray-300 shadow-sm rounded flex relative w-full">
                  <select
                    name="timeSlot"
                    required
                    id="timeSlot"
                    value={customer.TimeSlot === 0 ? "" : customer.TimeSlot}
                    className={`body-input-font background-white-ie-fix appearance-none z-10 py-3 pl-3 pr-12 overflow-ellipsis whitespace-nowrap overflow-x-hidden w-full border border-transparent focus:outline-none focus:border-${flavourConfig.inputColor} rounded`}
                    onChange={(e) =>
                      updateCustomer({
                        ...customer,
                        TimeSlot: parseInt(e.target.value),
                      })
                    }
                  >
                    <option disabled value=""></option>
                    {timeSlots.map(renderTimeSlotList)}
                  </select>
                  <div
                    className="px-4 flex items-center border-l border-gray-300 flex-col justify-center text-gray-500
                        absolute right-0 bottom-0 top-0 mx-auto z-20 pointer-events-none "
                  >
                    <svg
                      tabIndex={-1}
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-chevron-up"
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <polyline points="6 15 12 9 18 15" />
                    </svg>
                    <svg
                      tabIndex={-1}
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-chevron-down"
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
              </div>
            )}
            <div className="xl:w-2/5 lg:w-2/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="Name"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Full Name<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="Name"
                value={customer.CustomerName}
                required
                id="Name"
                className={`body-input-font border border-gray-300 pl-3 py-3 shadow-sm rounded focus:outline-none bg-transparent focus:border-${flavourConfig.inputColor}`}
                onChange={(e) =>
                  updateCustomer({
                    ...customer,
                    CustomerName: e.target.value,
                  })
                }
              />
            </div>
            <div className="xl:w-2/5 lg:w-2/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="StreetAddress"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Street Address<span className="text-red-600">*</span>
              </label>
              <AddressAutocomplete
                customer={customer}
                addressErrorMessage={addressErrorMessage}
                updateAddressErrorMessage={updateAddressErrorMessage}
                updateCustomer={updateCustomer}
                isValidAddress={isValidAddress}
                updateIsValidAddress={updateIsValidAddress}
              />
              {addressErrorMessage && (
                <p className="mt-2 body-error">{addressErrorMessage}</p>
              )}
            </div>
            <div className="xl:w-2/5 lg:w-2/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <label
                htmlFor="email2"
                className="pb-1.5 xl:pb-2 form-subheading-font"
              >
                Email Address<span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <div className="absolute text-gray-600 flex items-center px-4 border-r h-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-mail"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" />
                    <rect x={3} y={5} width={18} height={14} rx={2} />
                    <polyline points="3 7 12 13 21 7" />
                  </svg>
                </div>
                <input
                  id="email2"
                  name="email"
                  type="email"
                  value={customer.CustomerEmail}
                  required
                  className={`w-full body-input-font bg-transparent border-gray-300 focus:outline-none focus:border-${flavourConfig.inputColor} font-normal py-3 flex items-center pl-16 rounded border shadow-sm`}
                  onChange={(e) =>
                    updateCustomer({
                      ...customer,
                      CustomerEmail: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="xl:w-2/5 lg:w-2/5 md:w-2/5 flex flex-col mb-4 xl:mb-6">
              <div className="flex items-center">
                <label
                  htmlFor="Number"
                  className="pb-1.5 xl:pb-2 form-subheading-font"
                >
                  Phone Number<span className="text-red-600">*</span>
                </label>
              </div>
              <input
                type="tel"
                id="Number"
                name="Number"
                value={customer.CustomerPhone}
                required
                className={`border body-input-font pl-3 py-3 shadow-sm border-gray-300 rounded focus:outline-none bg-transparent focus:border-${flavourConfig.inputColor}`}
                onChange={(e) =>
                  updateCustomer({
                    ...customer,
                    CustomerPhone: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className="flex items-center pt-0 pb-4">
            <input
              type="checkbox"
              name="privacy-policy"
              id="privacy-policy"
              className="cursor-pointer"
              required
              checked={hasTickedPrivacyPolicy}
              onChange={(e) => {
                updateHasTickedPrivacyPolicy(!hasTickedPrivacyPolicy);
                (e.target as HTMLInputElement).setCustomValidity("");
              }}
              onInvalid={(e) =>
                (e.target as HTMLInputElement).setCustomValidity(
                  "You must agree to the privacy policy if you wish to continue.",
                )
              }
            />
            <label
              htmlFor="privacy-policy"
              className="ml-2 form-subheading-font"
            >
              I have read and agree to the{" "}
              <a
                href="https://cms.environz.co.nz/media/Enviro%20NZ%20Privacy%20Policy.pdf"
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              <span className="text-red-600">*</span>
            </label>
          </div>
        </div>
      )}
    </form>
  );
};

export default CustomerForm;
